// ÁTTEKINTÉS — „Ünnepeltek" csempe (a régi „Csapat" utódja): a mai nap kiemelve, alatta a következő napok.
//
// MIT MUTAT: születésnap · névnap · belépési évforduló · új belépő (üdvözlés) · a SAJÁT gyerekek születésnapja (a BFF
// csak a szülő saját kezdőlapjára adja — a kliens azt rendereli, amit kap). A naptár szerinti mai névnapok a tetején.
//
// AMI SOHA NINCS ITT: SZÜLETÉSI ÉV vagy ÉLETKOR. A válasz sem hordozza (minimál-projekció), a csempe a hónap-napot
// és a hátralévő napot mutatja. Az évforduló évszáma („5. évforduló") nem érzékeny adat.
//
// HANGVÉTEL: meleg, de visszafogott — a mai ünnepeltek puha, rózsaszín-borostyán lapra kerülnek nagyobb avatarral és
// üdvözlő felirattal (csapat-színű avatar, mint a címtárban); a közelgők kompakt sorok. Nincs konfetti, nincs emoji.
import { type CelebrationsResponseSchema } from '@atlas/contracts';
import { DASHBOARD_WINDOW_DAYS, type DashboardWindowDays } from '@atlas/shared';
import {
  CakeIcon,
  ChevronDownIcon,
  GiftIcon,
  HandRaisedIcon,
  SparklesIcon,
  TrophyIcon,
} from '@heroicons/react/20/solid';
import { type ComponentType, type SVGProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { CARD_HEADER_ACTION_CLASS } from '@/components/shared/card-open-all';
import { CardRow, CardRows } from '@/components/shared/card-rows';
import { PanelCard, PanelCardEmpty } from '@/components/shared/panel-card';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { dashboardKeys } from '@/features/dashboard/api';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';

type Celebrations = z.infer<typeof CelebrationsResponseSchema>;
type Item = Celebrations['items'][number];

/** Hány közelgő sort mutat a csempe összecsukva (a kezdőlap nem címtár). */
const UPCOMING_LIMIT = 6;
/** Hány naptári névnap-nevet írunk ki a sorban (a többi „+N", a teljes lista tooltipben). */
const CALENDAR_NAMES_SHOWN = 3;

const KIND_ICON: Record<Item['kind'], ComponentType<SVGProps<SVGSVGElement>>> = {
  birthday: CakeIcon,
  nameDay: SparklesIcon,
  anniversary: TrophyIcon,
  newJoiner: HandRaisedIcon,
  childBirthday: GiftIcon,
};
const KIND_TONE: Record<Item['kind'], string> = {
  birthday: 'text-pink',
  nameDay: 'text-warning',
  anniversary: 'text-brand',
  newJoiner: 'text-sky',
  childBirthday: 'text-pink',
};

/** `YYYY-MM-DD` + N nap → UTC-dátum (a nap-pontos érték zóna-konverzió nélkül). */
function addDays(day: string, n: number): Date {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

/** A sor SZÖVEGES felirata (a típus + a mai/közelgő változat). Életkor/évszám sosem kerül bele. */
function useKindLabel(): (item: Item, today: boolean) => string {
  const { t } = useTranslation();
  return (item, today) => {
    switch (item.kind) {
      case 'birthday':
        return t(
          today
            ? 'dashboard.celebrations.kind.birthdayToday'
            : 'dashboard.celebrations.kind.birthday',
        );
      case 'nameDay':
        return t(
          today
            ? 'dashboard.celebrations.kind.nameDayToday'
            : 'dashboard.celebrations.kind.nameDay',
        );
      case 'anniversary':
        return t(
          today
            ? 'dashboard.celebrations.kind.anniversaryToday'
            : 'dashboard.celebrations.kind.anniversary',
          { count: item.years ?? 1 },
        );
      case 'newJoiner':
        return item.daysUntil === 0
          ? t('dashboard.celebrations.kind.newJoinerToday')
          : t('dashboard.celebrations.kind.newJoinerPast', { count: Math.abs(item.daysUntil) });
      case 'childBirthday':
        return t(
          today
            ? 'dashboard.celebrations.kind.childBirthdayToday'
            : 'dashboard.celebrations.kind.childBirthday',
          { child: item.childGivenName ?? '' },
        );
    }
  };
}

function itemKey(i: Item): string {
  return `${i.kind}-${i.personId}-${i.childGivenName ?? ''}`;
}

/** A MAI ünnepelt: nagyobb avatar + üdvözlő felirat, puha lapon. */
function TodayRow({ item }: { item: Item }): JSX.Element {
  const { i18n } = useTranslation();
  const label = useKindLabel();
  const Icon = KIND_ICON[item.kind];
  const name = formatName(
    { givenName: item.givenName, familyName: item.familyName, displayName: item.name },
    i18n.language,
  );
  return (
    <li
      data-testid="celebration-today"
      className="from-pink-soft/70 to-warning/10 flex items-center gap-3 rounded-xl bg-gradient-to-r px-3 py-2.5"
    >
      <Avatar name={name} src={item.avatarUrl} size="lg" accentColor={item.teamColor} />
      <div className="min-w-0 flex-1">
        <div className="text-md text-foreground truncate font-semibold">{name}</div>
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <Icon className={`size-4 shrink-0 ${KIND_TONE[item.kind]}`} aria-hidden />
          <span className="min-w-0 truncate">{label(item, true)}</span>
        </div>
      </div>
    </li>
  );
}

/** Egy közelgő / múltbeli (új belépő) kompakt sor. */
function UpcomingRow({ item, today }: { item: Item; today: string }): JSX.Element {
  const { t, i18n } = useTranslation();
  const label = useKindLabel();
  const Icon = KIND_ICON[item.kind];
  const locale = i18n.language;
  const when =
    item.daysUntil < 0
      ? ''
      : item.daysUntil === 1
        ? t('dashboard.celebrations.tomorrow')
        : new Intl.DateTimeFormat(
            locale,
            item.daysUntil < 7
              ? { weekday: 'long', timeZone: 'UTC' }
              : { month: 'short', day: 'numeric', timeZone: 'UTC' },
          ).format(addDays(today, item.daysUntil));
  const name = formatName(
    { givenName: item.givenName, familyName: item.familyName, displayName: item.name },
    locale,
  );
  // UGYANAZ a felépítés, mint a mai ünnepelté (`TodayRow`), csak háttér nélkül: nagy profilkép elöl, mellette a
  // név, alatta az esemény ikonja + megnevezése, a sor végén a nap — korábban az ikon a profilkép elé csúszott,
  // a megnevezés pedig behúzással a név alá, ami elcsúszottnak hatott.
  return (
    <CardRow>
      <div data-testid="celebration-upcoming" className="flex items-center gap-3 px-4 py-2.5">
        <Avatar name={name} src={item.avatarUrl} size="lg" accentColor={item.teamColor} />
        <div className="min-w-0 flex-1">
          <div className="text-md text-foreground truncate font-semibold">{name}</div>
          <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <Icon className={`size-4 shrink-0 ${KIND_TONE[item.kind]}`} aria-hidden />
            <span className="min-w-0 truncate">{label(item, false)}</span>
          </div>
        </div>
        {when !== '' && (
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{when}</span>
        )}
      </div>
    </CardRow>
  );
}

export interface CelebrationsCardProps {
  windowDays: DashboardWindowDays;
  onWindowDaysChange: (days: DashboardWindowDays) => void;
}

export function CelebrationsCard({
  windowDays,
  onWindowDaysChange,
}: CelebrationsCardProps): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const q = api.dashboardCelebrations.useQuery(
    dashboardKeys.celebrations(windowDays),
    { query: { days: windowDays } },
    { queryKey: dashboardKeys.celebrations(windowDays) },
  );
  const body = q.data?.status === 200 ? q.data.body : null;
  const items = body?.items ?? [];
  const todayItems = items.filter((i) => i.daysUntil === 0);
  const upcoming = items
    .filter((i) => i.daysUntil !== 0)
    .sort((a, b) => Math.abs(a.daysUntil) - Math.abs(b.daysUntil) || a.name.localeCompare(b.name));
  const shown = expanded ? upcoming : upcoming.slice(0, UPCOMING_LIMIT);
  const names = body?.calendarNameDays ?? [];

  const windowMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={CARD_HEADER_ACTION_CLASS}
          aria-label={t('dashboard.celebrations.windowLabel')}
        >
          {t('dashboard.celebrations.window', { count: windowDays })}
          <ChevronDownIcon className="size-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {DASHBOARD_WINDOW_DAYS.map((d) => (
          <DropdownMenuItem key={d} onSelect={() => onWindowDaysChange(d)}>
            {t('dashboard.celebrations.window', { count: d })}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <PanelCard
      capped
      title={t('dashboard.tiles.celebrations.title')}
      loading={q.isLoading}
      skeletonRows={3}
      action={windowMenu}
    >
      {names.length > 0 && (
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-1 px-4 pb-1 pt-3 text-sm">
          <SparklesIcon className="text-warning size-4 shrink-0" aria-hidden />
          <span data-testid="calendar-namedays">
            {t('dashboard.celebrations.calendarToday', {
              names: names.slice(0, CALENDAR_NAMES_SHOWN).join(', '),
            })}
          </span>
          {names.length > CALENDAR_NAMES_SHOWN && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="text-foreground cursor-help font-medium">
                  {t('dashboard.celebrations.calendarMore', {
                    count: names.length - CALENDAR_NAMES_SHOWN,
                  })}
                </span>
              </TooltipTrigger>
              <TooltipContent>{names.join(', ')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
      {items.length === 0 ? (
        <PanelCardEmpty>
          <p>{t('dashboard.celebrations.empty', { count: windowDays })}</p>
        </PanelCardEmpty>
      ) : (
        <div>
          {todayItems.length > 0 && (
            <section aria-label={t('dashboard.celebrations.today')} className="p-3">
              <ul className="space-y-2">
                {todayItems.map((i) => (
                  <TodayRow key={itemKey(i)} item={i} />
                ))}
              </ul>
            </section>
          )}
          {upcoming.length > 0 && (
            <section>
              <Text variant="overline" as="h3" className="bg-muted/40 px-4 py-1.5">
                {t('dashboard.celebrations.upcoming')}
              </Text>
              <CardRows>
                {shown.map((i) => (
                  <UpcomingRow key={itemKey(i)} item={i} today={body?.today ?? ''} />
                ))}
              </CardRows>
              {upcoming.length > UPCOMING_LIMIT && (
                <div className="px-4 py-2">
                  <Button size="xs" variant="ghost" onClick={() => setExpanded((e) => !e)}>
                    {expanded
                      ? t('dashboard.celebrations.showLess')
                      : t('dashboard.celebrations.showAll', { count: upcoming.length })}
                  </Button>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </PanelCard>
  );
}
